/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

import { HIERARCHY_ITEM_STATUS } from '../../constants/constants';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchDelete, fetchGet, fetchPost, fetchPut } from 'FetchAPIs';
import { DimensionsType, DimensionType, EntityType } from 'EntitiesTypes';
import {
  AddDimensionsForDeviceTypeType,
  DeleteDimensionsForDeviceTypeType,
  DeleteMetricsForDeviceTypeType,
  DeviceTypeResourceType,
  LoadDevicesForDeviceTypeType,
  LoadDeviceTypeById,
  LoadDeviceTypesType,
  LoadDimensionsForDeviceTypeType,
  UpdateDeviceAliasType,
  UpdateDimensionsForDeviceTypeType,
  UpdateV1DeviceTypeType,
  UpdateV2DeviceTypeType,
} from 'HierarchyActionTypes';

const baseHierarchiesURL = '/api/hierarchies';
const deviceTypesV2URL = '/api/v2/deviceTypes';

const messages = defineMessages({
  errorLoadingDeviceTypes: 'Error loading device types. {message}',
  errorLoadingDeviceType: 'Error loading device type. {message}',
  errorLoadingDevicesOfDeviceTypes: 'Error loading devices of device types. {message}',
  errorLoadingDimensions: 'Error loading dimensions. {message}',
  errorUpdatingDimensionsForDeviceType: 'Error updating dimensions for device type. {message}',
  errorUpdatingMetricsForDeviceType: 'Error updating metrics for device type. {message}',
  errorDeletingMetricsForDeviceType: 'Error deleting metrics for device type. {message}',
  errorDeletingDimensionsForDeviceType: 'Error deleting dimensions for device type. {message}',
  errorCreatingDimensionsForDeviceType: 'Error creating dimensions for device type. {message}',
  errorUpdatingDeviceTypeData: 'Error updating device type data. {message}',
});

export const getDeviceTypesAPI = async (
  userEmailId: string,
  body = {},
  offset = 0,
  limit = 1000
): ReturnType<LoadDeviceTypesType> =>
  fetchPost(
    `${baseHierarchiesURL}/deviceTypes/search`,
    body,
    { userEmailId },
    { limit, status: HIERARCHY_ITEM_STATUS.ACTIVE, offset }
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDeviceTypes, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads details of one device type
 * @returns device type details
 */
export const getDeviceTypeByIdAPI = async (deviceTypeId: string): ReturnType<LoadDeviceTypeById> =>
  fetchGet(`${baseHierarchiesURL}/deviceTypes/${deviceTypeId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDeviceType, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads devices for a specific device type API
 * @param deviceTypeId - device type id
 * @returns existing devices for the specified device type
 */
export const getDevicesForDeviceType = async (
  deviceTypeId: string,
  page?: number,
  limit?: number,
  deviceIds: string = null
): ReturnType<LoadDevicesForDeviceTypeType> =>
  fetchGet(`${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices`, null, {
    page,
    limit,
    ...(deviceIds ? { deviceIds } : {}),
  })
    .then(response => response ?? { totalCount: 0, results: [] })
    .catch(error => {
      throw new I18NError(messages.errorLoadingDevicesOfDeviceTypes, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads dimensions for a specific device type API
 * @param deviceTypeId - device type id
 * @returns dimensions for the specified device type
 */
export const getDimensionsForDeviceType = async (
  deviceTypeId: string
): ReturnType<LoadDimensionsForDeviceTypeType> =>
  fetchGet(`${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/dimensions`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDimensions, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that updates dimensions for a specific device type API
 * @param deviceTypeUUID - device type uuid
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user updating the dimensions
 * @returns
 */
export const updateDimensionsForDeviceType = async (
  deviceTypeUUID: string,
  dimensions: DimensionsType,
  overrideExistingValue: boolean,
  userEmailId: string
): ReturnType<UpdateDimensionsForDeviceTypeType> => {
  const dimensionUpdatePromises: Array<Promise<DimensionType>> = [];
  dimensions.forEach(dimension => {
    dimensionUpdatePromises.push(
      fetchPut(
        `${baseHierarchiesURL}/deviceTypes/${deviceTypeUUID}/dimensions/${dimension.name}`,
        { ...dimension, overrideExistingValue },
        { userEmailId },
        null
      ).catch(error => {
        throw new I18NError(messages.errorUpdatingDimensionsForDeviceType, {
          message: formatErrorResponse(error),
        });
      })
    );
  });

  return Promise.all(dimensionUpdatePromises);
};

/**
 * Function that updates dimensions for a specific device type API
 * @param deviceTypeUUID - device type uuid
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user deleting the dimensions
 * @returns
 */
export const deleteDimensionsForDeviceType = async (
  deviceTypeUUID: string,
  dimensions: string[],
  userEmailId: string
): ReturnType<DeleteDimensionsForDeviceTypeType> =>
  fetchDelete(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeUUID}/dimensions`,
    null,
    { userEmailId },
    { dimensionNames: dimensions.join(', ') } // Need to send the dimension names as params delimited by a comma
  ).catch(error => {
    throw new I18NError(messages.errorDeletingDimensionsForDeviceType, {
      message: formatErrorResponse(error),
    });
  });

/**
 * Function that creates dimensions for a specific device type API
 * @param deviceTypeUUID - device type uuid
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user creating the dimensions
 * @returns
 */
export const createDimensionsForDeviceType = async (
  deviceTypeUUID: string,
  dimensions: DimensionsType,
  userEmailId: string
): ReturnType<AddDimensionsForDeviceTypeType> =>
  fetchPost(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeUUID}/dimensions`,
    dimensions,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorCreatingDimensionsForDeviceType, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that update the v2 device type
 * @param deviceTypeUUID - device type uuid
 * @param deviceTypeData - array of dimensions
 * @param userEmailId - email of the user creating the dimensions
 * @returns
 */
export const updateV2DeviceTypeAPI = async (
  deviceTypeUUID: string,
  deviceTypeData: EntityType,
  userEmailId: string
): ReturnType<UpdateV2DeviceTypeType> =>
  fetchPut(`${deviceTypesV2URL}/${deviceTypeUUID}`, deviceTypeData, { userEmailId }, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingMetricsForDeviceType, error);
    });

/**
 * Function that updates dimensions for a specific device type API
 * @param deviceTypeUUID - device type uuid
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user deleting the dimensions
 * @returns
 */
export const deleteMetricsForV2DeviceType = async (
  deviceTypeUUID: string,
  dataItemIds: string[],
  userEmailId: string
): ReturnType<DeleteMetricsForDeviceTypeType> =>
  fetchDelete(
    `${deviceTypesV2URL}/${deviceTypeUUID}/dataItems`,
    null,
    { userEmailId },
    { dataItemIds: dataItemIds.join(', ') }
  ).catch(error => {
    throw new I18NError(messages.errorDeletingMetricsForDeviceType, {
      message: formatErrorResponse(error),
    });
  });

/**
 * Function that updates alias for a device
 * @returns existing device details
 */
export const updateDeviceAlias = async (
  deviceTypeId: string,
  deviceId: string,
  alias: string,
  userEmailId: string
): ReturnType<UpdateDeviceAliasType> =>
  fetchPut(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices/${deviceId}/alias`,
    { alias, name: deviceId },
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDeviceTypes, error);
    });

/**
 * Function that updates device type API
 * @param deviceTypeId - device type id
 * @param deviceTypeData - device type data to be updated
 * @param userEmailId - email of the user updating the dimensions
 * @returns
 */
export const updateV1DeviceTypeAPI = async (
  deviceTypeId: string,
  deviceTypeData: DeviceTypeResourceType,
  userEmailId: string
): ReturnType<UpdateV1DeviceTypeType> =>
  fetchPut(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}`,
    deviceTypeData,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingDeviceTypeData, error);
    });
